import React, { useState, useEffect } from 'react'

const OccasionBadge = ({ 
  template, 
  size = 'sm', 
  showNextDate = true, 
  className = '' 
}) => {
  const [isApplicable, setIsApplicable] = useState(null)
  const [nextDate, setNextDate] = useState(null)
  const [loading, setLoading] = useState(false)

  // Check if template has occasion scheduling
  const hasOccasionScheduling = template?.isOccasionDateApplicable && template?.occasionType && template?.occasionType !== 'none'

  // Fetch current applicability and next date
  useEffect(() => {
    if (!hasOccasionScheduling || !template?.id) return

    const checkApplicability = async () => {
      setLoading(true)
      try {
        // Check current applicability
        const now = new Date().toISOString()
        const checkResponse = await fetch(
          `/api/v1/templates/${template.id}/occasion/check?check_date=${now}&timezone=${template.occasionTimezone || 'UTC'}`
        )
        
        if (checkResponse.ok) {
          const checkData = await checkResponse.json()
          setIsApplicable(checkData.data.isApplicable)
        }

        // Get next applicable date if showing next date
        if (showNextDate) {
          const nextResponse = await fetch(
            `/api/v1/templates/${template.id}/occasion/next-dates?limit=1`
          )
          
          if (nextResponse.ok) {
            const nextData = await nextResponse.json()
            const nextDates = nextData.data.nextDates
            if (nextDates && nextDates.length > 0) {
              setNextDate(new Date(nextDates[0]))
            }
          }
        }
      } catch (error) {
        console.error('Error checking occasion applicability:', error)
      } finally {
        setLoading(false)
      }
    }

    checkApplicability()
  }, [template?.id, hasOccasionScheduling, showNextDate, template?.occasionTimezone])

  // Don't render if no occasion scheduling
  if (!hasOccasionScheduling) {
    return null
  }

  const getOccasionIcon = (occasionType) => {
    switch (occasionType) {
      case 'specific_date': return '📅'
      case 'date_range': return '📊'
      case 'recurring_yearly': return '🔁'
      case 'weekly': return '📆'
      case 'monthly': return '🗓️'
      default: return '⏰'
    }
  }

  const getOccasionLabel = (occasionType) => {
    switch (occasionType) {
      case 'specific_date': return 'Specific Date'
      case 'date_range': return 'Date Range'
      case 'recurring_yearly': return 'Yearly'
      case 'weekly': return 'Weekly'
      case 'monthly': return 'Monthly'
      default: return 'Scheduled'
    }
  }

  const formatNextDate = (date) => {
    if (!date) return null
    
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays === -1) return 'Yesterday'
    if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`
    if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`
    
    return date.toLocaleDateString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    })
  }

  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-2.5 py-1.5',
    lg: 'text-sm px-3 py-2'
  }

  if (loading) {
    return (
      <div className={`inline-flex items-center ${sizeClasses[size]} bg-gray-100 text-gray-500 rounded-full ${className}`}>
        <div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin mr-1"></div>
        <span>Checking...</span>
      </div>
    )
  }

  return (
    <div className={`inline-flex flex-col gap-1 ${className}`}>
      {/* Main occasion badge */}
      <div className={`inline-flex items-center ${sizeClasses[size]} rounded-full font-medium ${
        isApplicable === true 
          ? 'bg-green-100 text-green-800 border border-green-200'
          : isApplicable === false
          ? 'bg-red-100 text-red-800 border border-red-200'
          : 'bg-blue-100 text-blue-800 border border-blue-200'
      }`}>
        <span className="mr-1">
          {getOccasionIcon(template.occasionType)}
        </span>
        <span>
          {getOccasionLabel(template.occasionType)}
        </span>
        
        {/* Current status indicator */}
        {isApplicable !== null && (
          <span className={`ml-1 w-2 h-2 rounded-full ${
            isApplicable ? 'bg-green-500' : 'bg-red-500'
          }`} />
        )}
      </div>

      {/* Next date badge */}
      {showNextDate && nextDate && (
        <div className={`inline-flex items-center ${sizeClasses[size]} bg-gray-100 text-gray-700 rounded-full`}>
          <span className="mr-1">📍</span>
          <span className="font-medium">
            {formatNextDate(nextDate)}
          </span>
        </div>
      )}

      {/* No upcoming dates warning */}
      {showNextDate && !nextDate && !loading && isApplicable === false && (
        <div className={`inline-flex items-center ${sizeClasses[size]} bg-yellow-100 text-yellow-800 rounded-full`}>
          <span className="mr-1">⚠️</span>
          <span>No upcoming dates</span>
        </div>
      )}
    </div>
  )
}

// Simplified version for compact display
export const OccasionIndicator = ({ template, className = '' }) => {
  const hasOccasionScheduling = template?.isOccasionDateApplicable && template?.occasionType && template?.occasionType !== 'none'
  
  if (!hasOccasionScheduling) {
    return null
  }

  const getOccasionIcon = (occasionType) => {
    switch (occasionType) {
      case 'specific_date': return '📅'
      case 'date_range': return '📊'
      case 'recurring_yearly': return '🔁'
      case 'weekly': return '📆'
      case 'monthly': return '🗓️'
      default: return '⏰'
    }
  }

  return (
    <div className={`inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full ${className}`} title={`Occasion: ${template.occasionType}`}>
      <span className="text-sm">
        {getOccasionIcon(template.occasionType)}
      </span>
    </div>
  )
}

export default OccasionBadge
